/**
 * 个人页面用户信息卡片组件
 * 可滚动消失的用户信息展示
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { User } from '../utils/auth';

interface UserInfoCardProps {
  user: User | null;
}

export const UserInfoCard: React.FC<UserInfoCardProps> = ({ user }) => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      user: { text: '会员', color: '#6b7280' },
      premium: { text: '超级会员', color: '#059669' },
      super: { text: '超级会员', color: '#7c3aed' },
      admin: { text: '管理者', color: '#dc2626' },
    };

    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.user;
    // fullText kept for potential detailed displays; use the mapped label directly
    return { ...config, fullText: config.text };
  };

  const roleBadge = getRoleBadge(user?.role || '');

  return (
    <div className="my-user-card">
      {/* Compact User Info Card */}
      <div className="my-user-card__content">
        <div className="my-user-card__main" onClick={() => setShowDetails(!showDetails)}>
          {/* Avatar */}
          <div className="my-user-card__avatar">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>

          {/* User Info */}
          <div className="my-user-card__info">
            <div className="my-user-card__username">{user?.username}</div>
            <div className="my-user-card__role-badge">
              {roleBadge.text}
            </div>
          </div>

          {/* Spacer to push buttons to the right */}
          <div className="my-user-card__spacer"></div>

          {/* Action Buttons - Now inline with main content */}
          <div className="my-user-card__actions">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate('/profile/edit');
              }}
              className="my-user-card__action-btn"
              title="编辑个人信息"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate('/profile/settings');
              }}
              className="my-user-card__action-btn"
              title="账户设置"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* Expandable Details */}
        <div className={`my-user-card__details ${showDetails ? 'visible' : ''}`}>
          <div className="my-user-card__detail-item">
            <span className="my-user-card__detail-label">用户ID</span>
            <span className="my-user-card__detail-value">{user?.id}</span>
          </div>
          <div className="my-user-card__detail-item">
            <span className="my-user-card__detail-label">邮箱</span>
            <span className="my-user-card__detail-value">{user?.email}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 个人页面Tab导航组件
 * 吸顶的tab切换栏
 */

interface MyStickyTabsProps {
  activeTab: 'management' | 'history';
}

export const MyStickyTabs: React.FC<MyStickyTabsProps> = ({ activeTab }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const jumpTab = (tab: 'management' | 'history') => {
    const currentTab = new URLSearchParams(location.search).get('tab') || 'management';
    if (currentTab === tab) return;

    navigate({ pathname: '/profile', search: `?tab=${tab}` });
  };

  return (
    <div className="my-sticky-tabs">
      <button
        className={`my-sticky-tabs__tab ${activeTab === 'management' ? 'active' : ''}`}
        onClick={() => jumpTab('management')}
        aria-pressed={activeTab === 'management'}
      >
        视频管理
      </button>
      <button
        className={`my-sticky-tabs__tab ${activeTab === 'history' ? 'active' : ''}`}
        onClick={() => jumpTab('history')}
        aria-pressed={activeTab === 'history'}
      >
        浏览历史
      </button>
    </div>
  );
};

